import { SensorsAccessoriesPageData } from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { PropsWithChildren, useCallback, useContext } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { calculateTotals } from 'src/sensors/checkout/CalculateTotals.ts';
import { Price } from 'src/common/Price/Price';
import { SelectionDataContext } from 'src/projects/components/SelectionDataContext.ts';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon.tsx';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { matchRoutes } from 'react-router-dom';
import { RouteObject } from 'react-router/dist/lib/context';
import { SensorRoutes } from 'src/sensors/router.tsx';
import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import { initLocationState, isLocationState } from 'src/sensors/locationStateContext.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { ExportTable } from 'shared/projects/ExportTable.ts';
import { Counter } from 'src/common/Counter/Counter.tsx';
import { updatedLocationState } from 'src/common/updatedLocationState.ts';
import { removeByLabel, updateQtyByLabel } from 'src/projects/utils/updateQtyByLabel.ts';
import { Button } from 'src/common/Button/Button.tsx';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon.tsx';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';

type FetchSensorSelectionProps = {
    searchParams: string;
    pathname: string;
    exportData: ExportTable;
    updateSelection: (searchParams: string, exportData: ExportTable) => void;
}

export const FetchSensorSelection = ({
    searchParams,
    pathname,
    exportData,
    updateSelection,
    children,
}: PropsWithChildren<FetchSensorSelectionProps>) => {
    const matches = matchRoutes<RouteObject>(SensorRoutes, pathname);

    if (matches === null) {
        throw new Error('match failed');
    }

    const [match] = matches;

    const { sensorLabel } = match.params;

    const locationState = readSearchParam(
        new URLSearchParams(searchParams),
        LOCATION_STORE_PARAM_NAME,
        { ...initLocationState },
        isLocationState,
    );

    if (sensorLabel !== undefined && locationState.sensorLabel !== sensorLabel) {
        locationState.sensorLabel = sensorLabel;
    }

    const { userSettings, currency } = useContext(ConfigContext);
    const ctx = useContext(SelectionDataContext);

    const onAccessoryDelete = useCallback((label: string, pipeConnectors: boolean) => {
        updateSelection(
            updatedLocationState(
                new URLSearchParams(searchParams),
                {
                    ...locationState,
                    pipeConnectors,
                    selectedAccessories: locationState
                        .selectedAccessories
                        .filter((accessory) => accessory
                            .label !== label),
                },
            ).toString(),
            removeByLabel(exportData, label),
        );
    }, [exportData, locationState, searchParams, updateSelection]);

    const onAccessoryChange = useCallback((quantity: number, label: string, pipeConnectors: boolean) => {
        updateSelection(
            updatedLocationState(
                new URLSearchParams(searchParams),
                {
                    ...locationState,
                    pipeConnectors,
                    selectedAccessories: locationState
                        .selectedAccessories.map(
                            (accessory) => {
                                if (accessory.label === label) {
                                    return {
                                        ...accessory,
                                        qty: quantity,
                                    };
                                }
                                return accessory;
                            },
                        ),
                },
            ).toString(),
            updateQtyByLabel(
                exportData,
                label,
                quantity,
            ),
        );
    }, [exportData, locationState, searchParams, updateSelection]);

    const data = (ctx?.data ?? undefined) as SensorsAccessoriesPageData | undefined;
    if (ctx === null || ctx.loading || data === undefined) {
        return <SkeletonSelection/>;
    }

    const priceCurrency = ctx.currency;
    const { sensor } = data.accessories;

    const { pricePerUnit } = calculateTotals(
        data.accessories,
        userSettings,
        locationState,
        currency,
        locationState.pipeConnectors,
    );

    const {
        isRecommended,
    } = parseRecommended(data.accessories.sensor.connectors);

    const sensorAccessories = prepareAccessories(
        data.accessories.accessories,
        locationState.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessories(a.accessory, b.accessory),
    );

    const pipeConnectors = sensorAccessories
        .filter(({ accessory }) => isRecommended(accessory.item.Label));

    const accessories = sensorAccessories
        .filter(({ accessory }) => !isRecommended(accessory.item.Label));

    return (
        <>
            <ProjectSelectionDeviceList
                title={<I18n messageId={MESSAGES.checkoutSensorsTitle}/>}
            >
                <ProjectSelectionDevice
                    productImage={(
                        <ProductImage
                            imgSrc={sensor.image?.raw.join('') || ''}
                        >
                            <ValveIcon/>
                        </ProductImage>
                    )}
                    productLink={(
                        <ViewProductLink
                            userSettings={userSettings}
                            code={sensor.Label}
                            content=""
                        >
                            <span className="vss-device__product-label">
                                {sensor.Label}
                            </span>

                            <div className="vss-device__product-icon">
                                <ArrowRightIcon/>
                            </div>
                        </ViewProductLink>
                    )}
                    productDescription={(
                        <RenderHtml
                            data={sensor.productType?.localized.join('') || ''}
                        />
                    )}
                    quantity={(
                        <Counter
                            size="sm"
                            value={locationState.qty}
                            onChange={(qty) => {
                                updateSelection(
                                    updatedLocationState(
                                        new URLSearchParams(searchParams),
                                        {
                                            ...locationState,
                                            qty,
                                        },
                                    ).toString(),
                                    pipeConnectors.reduce(
                                        (acc, curr) => updateQtyByLabel(
                                            acc,
                                            curr.accessory.item.Label,
                                            curr.qty * (locationState.pipeConnectors ? qty : 1),
                                        ),
                                        updateQtyByLabel(exportData, locationState.sensorLabel, qty),
                                    ),
                                );
                            }}
                        />
                    )}
                    price={(
                        <Price
                            price={pricePerUnit}
                            currency={priceCurrency}
                            locale={userSettings.currentLocale}
                        />
                    )}
                />
            </ProjectSelectionDeviceList>
            {children}
            {pipeConnectors.length ? (
                <ProjectSelectionDeviceList
                    title={<I18n messageId={MESSAGES.PipeConnectors}/>}
                    actions={(
                        <Button
                            kind="ghost"
                            size="md"
                            icon={<DeleteIcon/>}
                            iconOnly={true}
                            onClick={() => {
                                updateSelection(
                                    updatedLocationState(
                                        new URLSearchParams(searchParams),
                                        {
                                            ...locationState,
                                            pipeConnectors: false,
                                            selectedAccessories: locationState
                                                .selectedAccessories
                                                .filter((accessory) => pipeConnectors
                                                    .find((connector) => accessory
                                                        .label === connector.accessory.item.Label) === undefined),
                                        },
                                    ).toString(),
                                    pipeConnectors.reduce(
                                        (acc, curr) => removeByLabel(acc, curr.accessory.item.Label),
                                        exportData,
                                    ),
                                );
                            }}
                        />
                    )}
                >
                    {pipeConnectors.map(({ qty, accessory: { item }, price }) => (
                        <ProjectSelectionDevice
                            key={item.Label}
                            productImage={(
                                <ProductImage
                                    imgSrc={item.image?.raw.join('') || ''}
                                >
                                    <ActuatorIcon/>
                                </ProductImage>
                            )}
                            productLink={(
                                <ViewProductLink
                                    userSettings={userSettings}
                                    code={item.Label}
                                    content=""
                                >
                                    {item.Label}

                                    <div className="vss-accessory__product-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                </ViewProductLink>
                            )}
                            productDescription={item.accessoryType?.localized.join('') || ''}
                            quantity={(
                                <Counter
                                    size="sm"
                                    value={qty * (locationState.pipeConnectors ? locationState.qty : 1)}
                                    onDelete={() => onAccessoryDelete(item.Label, false)}
                                    onChange={(
                                        quantity,
                                    ) => onAccessoryChange(quantity, item.Label, false)}
                                />
                            )}
                            price={(
                                <Price
                                    price={price}
                                    currency={priceCurrency}
                                    locale={userSettings.currentLocale}
                                />
                            )}
                        />
                    ))}

                </ProjectSelectionDeviceList>
            ) : null}

            {accessories.length ? (
                <ProjectSelectionDeviceList
                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                >
                    {accessories.map(({ qty, accessory: { item }, price }) => (
                        <ProjectSelectionDevice
                            key={item.Label}
                            productImage={(
                                <ProductImage
                                    imgSrc={item.image?.raw.join('') || ''}
                                >
                                    <ActuatorIcon/>
                                </ProductImage>
                            )}
                            productLink={(
                                <ViewProductLink
                                    userSettings={userSettings}
                                    code={item.Label}
                                    content=""
                                >
                                    {item.Label}

                                    <div className="vss-accessory__product-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                </ViewProductLink>
                            )}
                            productDescription={item.accessoryType?.localized.join('') || ''}
                            quantity={(
                                <Counter
                                    size="sm"
                                    value={qty}
                                    onDelete={() => onAccessoryDelete(item.Label, locationState.pipeConnectors)}
                                    onChange={(
                                        quantity,
                                    ) => onAccessoryChange(quantity, item.Label, locationState.pipeConnectors)}
                                />
                            )}
                            price={(
                                <Price
                                    price={price}
                                    currency={priceCurrency}
                                    locale={userSettings.currentLocale}
                                />
                            )}
                        />
                    ))}

                </ProjectSelectionDeviceList>
            ) : null}
        </>
    );
};
